import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BankAccountsApiAdapter } from '../../adapters/BankAccountsApiAdapter';
import { useBankAccountMutations } from '../../hooks/useBankAccountMutations';
import type { BankAccount, PayerBank, PayerBanksResponse } from '../../models/BankAccount';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { getErrorMessage } from '@shared/utils/error.utils';
import { useQuery } from '@tanstack/react-query';

export interface VerifyBankAccountModalRef {
  open: (account: BankAccount) => void;
  close: () => void;
}

interface VerifyBankAccountModalProps {
  onSuccess?: () => void;
}

export const VerifyBankAccountModal = forwardRef<VerifyBankAccountModalRef, VerifyBankAccountModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('bankAccountsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const { verifyBankAccount } = useBankAccountMutations();

    const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
    const bankAccountsAdapter = useMemo(() => new BankAccountsApiAdapter(httpClient), [httpClient]);

    const [account, setAccount] = useState<BankAccount | null>(null);
    const [payerBankId, setPayerBankId] = useState<number | null>(null);

    const {
      data: payerBanksData,
      isLoading: payerBanksLoading,
      isError: payerBanksError,
    } = useQuery<PayerBanksResponse>({
      queryKey: ['bankAccounts', 'payer-banks'],
      queryFn: () =>
        bankAccountsAdapter.getPayerBanks({
          perPage: 100,
          sorting: { column: 'name', direction: 'asc' },
        }),
      staleTime: 60 * 1000,
      enabled: account !== null,
    });

    const payerBanks: PayerBank[] = payerBanksData?.data ?? [];
    const hasPayerBanks = payerBanks.length > 0;

    useImperativeHandle(ref, () => ({
      open: (acc: BankAccount) => {
        setAccount(acc);
        setPayerBankId(null);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const resetModalState = () => {
      setAccount(null);
      setPayerBankId(null);
    };

    const handleVerify = async () => {
      if (!account || payerBankId === null) return;

      try {
        await verifyBankAccount.mutateAsync({
          id: account.id,
          payload: { payer_bank_id: payerBankId },
        });

        toast({
          title: t('modals.verify.success.title'),
          description: t('modals.verify.success.description'),
        });

        modalRef.current?.closeModal();
        resetModalState();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.verifyFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isVerifying = verifyBankAccount.isPending;
    const canSubmit =
      account !== null &&
      payerBankId !== null &&
      hasPayerBanks &&
      !payerBanksLoading &&
      !payerBanksError &&
      !isVerifying;

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modals.verify.title')}
        width="default"
        onCloseModal={resetModalState}
        classNameBody="min-h-0"
      >
        <div className="space-y-4 pb-1">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center transition-colors">
              <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-foreground">{t('modals.verify.description')}</p>
              <p className="text-sm text-muted-foreground mt-2">{t('modals.verify.info')}</p>
            </div>
          </div>

          {account && (
            <div className="bg-muted rounded-lg p-3 space-y-1 transition-colors">
              <p className="text-sm">
                <span className="font-medium">{t('modals.verify.accountSummary.holder')}</span> {account.holder_name}
              </p>
              <p className="text-sm font-mono">
                <span className="font-medium font-sans">{t('modals.verify.accountSummary.iban')}</span> {account.iban}
              </p>
              <p className="text-sm">
                <span className="font-medium">{t('modals.verify.accountSummary.bank')}</span> {account.bank_name}
              </p>
            </div>
          )}

          {payerBanksError && (
            <Alert variant="destructive">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
              <AlertTitle>{t('modals.verify.payerBankLoadErrorTitle')}</AlertTitle>
              <AlertDescription>{t('modals.verify.payerBankLoadErrorDescription')}</AlertDescription>
            </Alert>
          )}

          {!payerBanksLoading && !payerBanksError && !hasPayerBanks && account !== null && (
            <Alert variant="destructive" className="border-warning/80 bg-warning/10">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-warning mt-0.5" />
                <div className="flex-1">
                  <AlertTitle className="text-warning text-base !mb-1">
                    {t('modals.verify.noPayerBanks.title')}
                  </AlertTitle>
                  <AlertDescription className="text-sm">{t('modals.verify.noPayerBanks.description')}</AlertDescription>
                </div>
              </div>
            </Alert>
          )}

          <div className="space-y-1.5" data-testid="verify-payer-bank-field">
            <label className="text-sm font-medium text-foreground" htmlFor="verify-payer-bank-select">
              {t('modals.verify.fields.payer_bank_id')} *
            </label>
            <Select
              value={payerBankId !== null ? String(payerBankId) : ''}
              onValueChange={(value) => setPayerBankId(Number(value))}
              disabled={account === null || isVerifying || payerBanksLoading || !hasPayerBanks || payerBanksError}
            >
              <SelectTrigger
                id="verify-payer-bank-select"
                className="w-full min-h-10 py-2"
                data-testid="verify-payer-bank-select"
              >
                <SelectValue
                  placeholder={
                    payerBanksLoading ? t('loading.payerBanks') : t('modals.verify.fields.payer_bank_id_placeholder')
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-w-[min(100vw-2rem,var(--radix-select-trigger-width))] z-[120]">
                {payerBanks.map((bank) => (
                  <SelectItem
                    key={bank.id}
                    value={String(bank.id)}
                    textValue={[bank.name, bank.accounting_code].filter(Boolean).join(' · ')}
                  >
                    <div className="flex flex-col gap-0.5 py-0.5">
                      <span className="text-sm font-medium">{bank.name}</span>
                      <span className="text-xs text-muted-foreground font-mono">{bank.accounting_code}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isVerifying}>
              {t('buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleVerify}
              disabled={!canSubmit}
              className="bg-green-600 hover:bg-green-700"
            >
              {isVerifying ? t('buttons.verifying') : t('actions.verify')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

VerifyBankAccountModal.displayName = 'VerifyBankAccountModal';
